import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { Box, keyframes } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { CustomTypography } from "../formsComponents";
import { Dialog } from "./Dialog";
import { IAlarmDialogProps } from "./interfaces/IDialogProps";
import { useDialogStyles } from "./useDialogStyles";

import alarm from "../../assets/audios/alarm-clock.mp3";

// Define keyframe animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px #ff5252, 0 0 10px #ff5252, 0 0 15px #ff5252;
  }
  50% {
    box-shadow: 0 0 10px #ff5252, 0 0 20px #ff5252, 0 0 30px #ff5252;
  }
`;

const slideIn = keyframes`
  0% {
    transform: translateY(-20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

export const AlarmDialog: React.FC<IAlarmDialogProps> = (props) => {
  const { open, content, onClose } = props;
  const classes = useDialogStyles();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (open) {
      // Play alarm sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(console.error);
      }
      
      // Add vibration if supported
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200, 100, 200]);
      }
    } else {
      // Stop alarm sound
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        title="New Notification"
        onClose={onClose}
        size="sm"
        actions={[
          {
            type: "button",
            variant: "contained",
            label: "Got it!",
            onClick: onClose,
           
          },
        ]}
      >
        <audio ref={audioRef} src={alarm} preload="auto" />
        
        {/* Animated background ring */}
        <Box
          component="div"
          position="absolute"
          top="50%"
          left="50%"
          sx={{
            background: 'radial-gradient(circle, rgba(255,82,82,0.1) 0%, rgba(255,82,82,0.05) 70%, transparent 100%)',
            animation: `${pulse} 2s ease-in-out infinite`,
            zIndex: 0,
            transform: 'translate(-50%, -50%)'
          }}
          width="120px"
          height="120px"
          borderRadius="50%"
         
        />
        
        {/* Main icon container */}
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center"
          position="relative"
          sx={{ 
            mb: 4,
            mt: 2,
            zIndex: 1
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="80px"
            height="80px"
            borderRadius="50%"
            sx={{
              background: 'linear-gradient(135deg, #ff5252, #ff1744)',
              animation: `${glow} 1.5s ease-in-out infinite`,
              position: 'relative'
            }}
          >
            <NotificationsActiveIcon 
              sx={{ 
                fontSize: '2.5rem',
                color: 'white',
                animation: `${shake} 0.8s ease-in-out infinite`,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }}
            />
          </Box>
        </Box>

        {/* Content with slide-in animation */}
        <Box
          sx={{
            animation: `${slideIn} 0.6s ease-out`,
            position: 'relative',
            zIndex: 1
          }}
        >
          <CustomTypography 
            variant="h6" 
            textAlign="center" 
            sx={{ 
              mb: 2,
              fontWeight: 600,
              color: '#333',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
          >
            {content}
          </CustomTypography>
          
          <CustomTypography 
            variant="body2" 
            textAlign="center" 
            sx={{ 
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            Click "Got it!" to dismiss this notification
          </CustomTypography>
        </Box>
      </Dialog>
    </>
  );
};
